import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { SurveyHardMetrics } from 'services/types/survey';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface SurveysMetricsCardsProps {
  metrics: SurveyHardMetrics | undefined;
  isLoading: boolean;
}

interface MetricCardProps {
  title: string;
  value: number | string;
  isPercentage?: boolean;
  isLoading: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, isPercentage, isLoading }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      minHeight: 107,
      borderRadius: RADIUS_COIN,
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      boxShadow: SHADOW,
      overflow: 'visible',
    }}
  >
    <CardContent
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        '&:last-child': { pb: 2 },
      }}
    >
      {isLoading ? (
        <>
          <Skeleton variant="text" width="70%" sx={{ fontSize: '0.875rem' }} />
          <Skeleton variant="text" width="50%" sx={{ mx: 'auto', mt: 1, fontSize: '1.5rem' }} />
        </>
      ) : (
        <>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: `${NAVY}8C`,
              lineHeight: 1.3,
              wordBreak: 'break-word',
              flexShrink: 0,
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 40,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                color: NAVY,
                fontSize: '1.5rem',
                fontWeight: 700,
                lineHeight: 1.1,
                textAlign: 'center',
              }}
            >
              {isPercentage ? `${value}%` : value}
            </Typography>
          </Box>
        </>
      )}
    </CardContent>
  </Card>
);

const SurveysMetricsCards: React.FC<SurveysMetricsCardsProps> = ({ metrics, isLoading }) => {
  const metricsConfig = [
    { title: 'Encuestas Cargadas', value: metrics?.totalLoaded ?? 0 },
    { title: 'Encuestas Enviadas', value: metrics?.totalTriggered ?? 0 },
    { title: 'Pendientes de Respuesta', value: metrics?.totalPending ?? 0 },
    { title: 'Encuestas Completadas', value: metrics?.totalFinished ?? 0 },
    { title: 'Rechazadas', value: metrics?.totalAborted ?? 0 },
    { title: 'Tasa de Rebote', value: metrics?.bounceRate ?? 0, isPercentage: true },
    { title: 'Tasa de Respuesta', value: metrics?.responseRate ?? 0, isPercentage: true },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 600,
          fontSize: '1.1rem',
          color: NAVY,
          mb: 2,
        }}
      >
        Resumen de Encuestas
      </Typography>
      <Grid container spacing={2}>
        {metricsConfig.map((config) => (
          <Grid size={{ xs: 6, sm: 4, md: 12 / 7 }} key={config.title}>
            <MetricCard
              title={config.title}
              value={config.value}
              isPercentage={config.isPercentage}
              isLoading={isLoading}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SurveysMetricsCards;
